import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  input,
  OnInit,
  output,
  Signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminShippingMethodPriceFormGroup } from '../../models/admin-shipping-method-form-data';
import {
  Guid,
  isEmptyString,
  isNil,
  isNotNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import {
  FormControlComponent,
  NoDataPipe,
  PrimengButtonDirective,
  PrimengMultiselectDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { Select, SelectFilterEvent } from 'primeng/select';
import {
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { Button } from 'primeng/button';
import { ReactiveFormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { AdminDictionaryCountrySelectItemsProviderService } from '@web/shared/module/dictionary';
import { MultiSelect } from 'primeng/multiselect';
import { AdminPaymentMethodSelectItemsProviderService } from '../../providers/admin-payment-method-select-items-provider.service';

@Component({
  selector: 'app-admin-shipping-method-form-price-panel',
  imports: [
    CommonModule,
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    Button,
    ReactiveFormsModule,
    InputText,
    FormControlComponent,
    PrimengSelectDirective,
    Select,
    PrimengButtonDirective,
    NoDataPipe,
    MultiSelect,
    PrimengMultiselectDirective,
  ],
  templateUrl: './admin-shipping-method-form-price-panel.component.html',
  styleUrl: './admin-shipping-method-form-price-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminShippingMethodFormPricePanelComponent implements OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  /* prettier-ignore */
  private readonly dictionaryCountrySelectItemsProvider = inject(AdminDictionaryCountrySelectItemsProviderService);
  /* prettier-ignore */
  private readonly paymentMethodSelectItemsProvider = inject(AdminPaymentMethodSelectItemsProviderService);

  /* prettier-ignore */
  public readonly priceFormGroup = input.required<AdminShippingMethodPriceFormGroup>();
  public readonly index = input.required<number>();
  public readonly total = input.required<number>();
  public readonly removeClickEvent = output<number>();
  protected readonly panelCount = this.panelCountSignal();
  protected readonly shouldRemove = this.shouldRemoveSignal();
  protected readonly valueOrDefault = valueOrDefault;
  /* prettier-ignore */
  protected paymentMethodSelectItems$: Observable<SelectItem<Guid>[]> = this.paymentMethodsSelectItems();
  protected currentName$!: Observable<Nillable<string>>;
  protected countrySelectItems$: Observable<SelectItem<Guid>[]> = of([]);

  public constructor() {
    effect(() => this.configureProductSelectItemsSource());
  }

  public ngOnInit(): void {
    this.configureCurrentNameSource();
  }

  public handlePriceRemoveClick(): void {
    this.removeClickEvent.emit(this.index());
  }

  public handleSelectFilterChange(event: SelectFilterEvent): void {
    this.countrySelectItems$ = this.countrySelectItems(
      event.filter.toLowerCase(),
    );

    this.changeDetectorRef.detectChanges();
  }

  private configureProductSelectItemsSource(): void {
    this.countrySelectItems$ = this.countrySelectItems();
    this.changeDetectorRef.detectChanges();
  }

  private currentCountrySelectItem(): Observable<Nillable<SelectItem<Guid>>> {
    const group = this.priceFormGroup();
    const control = group.controls.countryId;

    return control.valueChanges.pipe(
      startWith(control.value),
      switchMap((id) => {
        if (!isNil(id) && !isEmptyString(id)) {
          return this.dictionaryCountrySelectItemsProvider
            .fetchSelectItems('id')
            .pipe(map((items) => items.find((item) => item.value === id)));
        }

        return of(null);
      }),
    );
  }

  private countrySelectItems(
    query: Nillable<string> = null,
  ): Observable<SelectItem<Guid>[]> {
    return this.currentCountrySelectItem().pipe(
      switchMap((currentSelectItem) => {
        return this.dictionaryCountrySelectItemsProvider
          .fetchSelectItems('id', query)
          .pipe(
            map((selectItems) => {
              if (isNil(currentSelectItem)) {
                return selectItems;
              }

              const isCurrentExists = isNotNil(
                selectItems.find(
                  (item) => item.value === currentSelectItem.value,
                ),
              );

              if (isCurrentExists) {
                return selectItems;
              }

              return [...selectItems, currentSelectItem];
            }),
          );
      }),
    );
  }

  private panelCountSignal(): Signal<string> {
    return computed(() => `#${this.index() + 1}`);
  }

  private shouldRemoveSignal(): Signal<boolean> {
    return computed(() => this.total() !== 1);
  }

  private configureCurrentNameSource(): void {
    this.currentName$ = this.currentCountrySelectItem().pipe(
      map((selectItems) => selectItems?.label),
    );
  }

  private paymentMethodsSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.paymentMethodSelectItemsProvider.fetchSelectItems('id');
  }
}
